import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import {
  ArrowLeft, ArrowRight, LayoutGrid, CalendarDays, Users, MapPin, Sparkles,
  Building2, Sun, PartyPopper, Mic, Rocket, Compass, Euro, Mail, CalendarIcon, X, CheckCircle, Star } from
"lucide-react";
import { cn } from "@/lib/utils";
import { sendSlackMessage } from "@/lib/sendSlackMessage";
import { getStoredUTMParams } from "@/lib/analytics";
import posthog from "posthog-js";
import confetti from "canvas-confetti";

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = [
{ label: "Type", icon: LayoutGrid },
{ label: "Dates", icon: CalendarDays },
{ label: "Catégories", icon: Compass },
{ label: "Participants", icon: Users },
{ label: "Standing", icon: Star },
{ label: "Votre match", icon: MapPin }];

const STEP_TITLES = [
"Quel type d'événement organisez-vous ?",
"Quand souhaitez-vous organiser votre événement ?",
"Quel type de destination recherchez-vous ?",
"Combien de participants prévoyez-vous ?",
"Quel standing souhaitez-vous ?",
"",
];

const STEP_LABELS = [
"Étape 1/6 — C'est parti !",
"Étape 2/6 — Vous êtes sur la bonne voie !",
"Étape 3/6 — On avance bien !",
"Étape 4/6 — Plus que 2 étapes !",
"Étape 5/6 — On y est presque !",
"Étape 6/6 — Dernière ligne droite !"];


const COMPATIBILITY_PERCENTS = [17, 34, 51, 68, 85, 100];

const EVENT_TYPES = [
{ value: "seminaire", label: "Séminaire d'entreprise", icon: Building2 },
{ value: "journee_hors_site", label: "Journée hors site", icon: Sun },
{ value: "soiree", label: "Soirée d'entreprise", icon: PartyPopper },
{ value: "team_building", label: "Activité team building", icon: Users },
{ value: "conference", label: "Conférence", icon: Mic },
{ value: "lancement_produit", label: "Lancement de produit", icon: Rocket }];


const MONTHS = [
"Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
"Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];


const CATEGORIES = [
{ emoji: "🏖️", label: "Plage" },
{ emoji: "🏔️", label: "Montagne" },
{ emoji: "🏙️", label: "Ville" },
{ emoji: "🌿", label: "Nature" },
{ emoji: "🌵", label: "Désert" },
{ emoji: "🌴", label: "Tropical" },
{ emoji: "🚤", label: "Bord de lac" },
{ emoji: "🏝️", label: "Île" },
{ emoji: "🏰", label: "Historique" },
{ emoji: "✨", label: "Luxe" },
{ emoji: "🎯", label: "Aventure" },
{ emoji: "🧘", label: "Bien-être" }];


const PARTICIPANT_RANGES = [
{ value: "10-30", label: "10 à 30", icon: "👥" },
{ value: "30-60", label: "30 à 60", icon: "🏡" },
{ value: "60-100", label: "60 à 100", icon: "🏘️" },
{ value: "100-200", label: "100 à 200", icon: "🏢" },
{ value: "200-500", label: "200 à 500", icon: "🏟️" }];


const BUDGETS = [
{ value: "less_10k", label: "Essentiel", amount: "< 10K€", stars: 1, min: 0, max: 10000 },
{ value: "10k_20k", label: "Confort", amount: "10–20K€", stars: 2, min: 10000, max: 20000 },
{ value: "20k_35k", label: "Premium", amount: "20–35K€", stars: 3, min: 20000, max: 35000 },
{ value: "35k_50k", label: "Prestige", amount: "35–50K€", stars: 4, min: 35000, max: 50000 },
{ value: "50k_plus", label: "Exception", amount: "> 50K€", stars: 5, min: 50000, max: 100000 }];


const formatBudgetLabel = (budgetValue: string): string => {
  const found = BUDGETS.find((b) => b.value === budgetValue);
  return found ? `${"⭐".repeat(found.stars)} ${found.label} (${found.amount})` : budgetValue;
};

const formatEventTypeLabel = (eventValue: string): string => {
  if (eventValue === "autre") return "Autre";
  const found = EVENT_TYPES.find((t) => t.value === eventValue);
  return found ? found.label : eventValue;
};

const FREE_DOMAINS = ["gmail.com","googlemail.com","hotmail.com","hotmail.fr","hotmail.co.uk","hotmail.it","hotmail.es","hotmail.de","hotmail.be","outlook.com","outlook.fr","outlook.es","outlook.it","outlook.de","outlook.be","outlook.co.uk","live.com","live.fr","live.be","live.co.uk","msn.com","yahoo.com","yahoo.fr","yahoo.co.uk","yahoo.es","yahoo.it","yahoo.de","yahoo.ca","ymail.com","rocketmail.com","icloud.com","me.com","mac.com","aol.com","aol.fr","laposte.net","orange.fr","wanadoo.fr","sfr.fr","neuf.fr","club-internet.fr","cegetel.net","numericable.fr","free.fr","aliceadsl.fr","libertysurf.fr","infonie.fr","bbox.fr","tele2.fr","protonmail.com","protonmail.ch","proton.me","pm.me","zoho.com","gmx.fr","gmx.com","gmx.net","gmx.de","mail.com","yandex.com","yandex.fr","yandex.ru","inbox.com","bluewin.ch","t-online.de","web.de","posteo.net","posteo.de","caramail.com","netcourrier.com","keemail.me","tutanota.com","tutanota.de","tuta.io","ueir.jci","voila.fr","noos.fr","skynet.be","telenet.be","proximus.be","belgacom.net","scarlet.be","voo.be","hispeed.ch","sunrise.ch","gmx.ch","swisscom.ch","virgilio.it","libero.it","tiscali.it","tin.it","alice.it","fastwebnet.it","terra.es","telefonica.net","ya.ru","mail.ru","rambler.ru","list.ru","comcast.net","att.net","sbcglobal.net","verizon.net","cox.net","charter.net","earthlink.net","optonline.net","centurylink.net"];

const isProEmail = (email: string) => {
  const domain = email.split("@")[1];
  return domain && !FREE_DOMAINS.includes(domain.toLowerCase());
};

const STEP_IMAGES = [
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75", name: "L'Abbaye de Royaumont", location: "Île-de-France" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2F2FYH1677762931205.jpg&w=2048&q=75", name: "Château d'Ermenonville", location: "Hauts-de-France" },
{ url: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/402466422.jpg", name: "Youza Ecolodge", location: "Île-de-France" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FoxHh1673372697492.jpg&w=2048&q=75", name: "Etiolles Country Club", location: "Île-de-France" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FC9co1677753991331.jpg&w=2048&q=75", name: "Domaine Rocabella", location: "Provence-Alpes-Côte d'Azur" },
{ url: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FfLhq1696321607168.jpg&w=2048&q=75", name: "Hôtel Mercure Chantilly", location: "Hauts-de-France" }];


const getRandomStepImages = () => {
  const shuffled = [...STEP_IMAGES].sort(() => Math.random() - 0.5);
  return shuffled;
};

// Animated counter hook
function useAnimatedCounter(target: number, duration = 600) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const start = value;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.round(start + (target - start) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {if (rafRef.current) cancelAnimationFrame(rafRef.current);};
  }, [target, duration]);

  return value;
}

const QuizModal: React.FC<QuizModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const [animKey, setAnimKey] = useState(0);
  const [bouncedItem, setBouncedItem] = useState<string | null>(null);
  const [eventType, setEventType] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);
  const [participants, setParticipants] = useState("");
  const [customParticipants, setCustomParticipants] = useState("");
  const [budget, setBudget] = useState("");
  const [customBudget, setCustomBudget] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepImages] = useState(() => getRandomStepImages());

  const targetPercent = step <= 5 ? COMPATIBILITY_PERCENTS[step] ?? 0 : 100;
  const displayedPercent = useAnimatedCounter(targetPercent);

  const triggerBounce = useCallback((id: string) => {
    setBouncedItem(id);
    setTimeout(() => setBouncedItem(null), 300);
  }, []);

  // Confetti on step 6
  useEffect(() => {
    if (step === 6) {
      const end = Date.now() + 1500;
      const interval = setInterval(() => {
        if (Date.now() > end) {clearInterval(interval);return;}
        confetti({
          particleCount: 30,
          spread: 80,
          origin: { x: Math.random(), y: Math.random() * 0.6 },
          colors: ["#C6E278", "#4CAF50", "#FFD700", "#FF6B6B", "#42A5F5"],
          disableForReducedMotion: true
        });
      }, 200);
      return () => clearInterval(interval);
    }
  }, [step]);


  const toggleMonth = (m: string) =>
  setSelectedMonth((prev) => prev === m ? "" : m);

  const toggleCategory = (c: string) => {
    setCategories((prev) => {
      if (prev.includes(c)) return prev.filter((x) => x !== c);
      if (prev.length >= 4) return prev;
      return [...prev, c];
    });
    triggerBounce(`cat-${c}`);
  };

  const validateEmail = () => {
    if (!email) {setEmailError("L'email est requis");return false;}
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {setEmailError("Adresse e-mail incorrecte");return false;}
    if (!isProEmail(email)) {setEmailError("Veuillez renseigner un email professionnel");return false;}
    setEmailError("");
    return true;
  };

  const canProceed = () => {
    switch (step) {
      case 0:return !!eventType;
      case 1:return !!selectedMonth || !!dateRange?.from;
      case 2:return categories.length > 0;
      case 3:return participants === "custom" ? !!customParticipants : !!participants;
      case 4:return budget === "custom" ? !!customBudget : !!budget;
      default:return true;
    }
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setDirection("forward");
      setAnimKey((k) => k + 1);
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setDirection("backward");
      setAnimKey((k) => k + 1);
      setStep((s) => s - 1);
    }
  };

  const handleStepClick = (i: number) => {
    if (i < step && step <= 5) {
      setDirection("backward");
      setAnimKey((k) => k + 1);
      setStep(i);
    }
  };

  const handleClose = () => {
    setStep(0);setEventType("");setDateRange(undefined);setSelectedMonth("");
    setCategories([]);setParticipants("");setCustomParticipants("");setBudget("");setCustomBudget("");setEmail("");setEmailError("");
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmitQuiz = async () => {
    if (!validateEmail()) return;
    setIsSubmitting(true);

    try {
      const storedUTMParams = getStoredUTMParams();
      const urlParams = new URLSearchParams(window.location.search);
      const sdr = urlParams.get('utm_sdr') || storedUTMParams.utm_sdr || '—';

      const participantsDisplay = participants === "custom" ? customParticipants : participants;
      const budgetDisplay = budget === "custom" ? customBudget : formatBudgetLabel(budget);
      const eventTypeDisplay = formatEventTypeLabel(eventType);

      // Format dates
      let dateDisplay = "Non spécifiées";
      if (dateRange?.from) {
        const fmt = (d: Date) => d.toLocaleDateString("fr-FR");
        dateDisplay = dateRange.to ? `du ${fmt(dateRange.from)} au ${fmt(dateRange.to)}` : `le ${fmt(dateRange.from)}`;
      } else if (selectedMonth) {
        dateDisplay = selectedMonth;
      }

      const slackMessage = `*Nouvelle demande (Quiz Destinations)*\n*Canal d'acquisition* : Quiz Destinations\n*Market* : FR 🇫🇷\n*SDR* : ${sdr}\n*Source du lead* : Landing Lovable – Quiz\n*Type d'event* : ${eventTypeDisplay}\n*Dates* : ${dateDisplay}\n\n*E-mail pro* : ${email}\n*Catégories sélectionnées* : ${categories.join(", ")}\n*Participants* : ${participantsDisplay}\n*Budget* : ${budgetDisplay}`;

      await sendSlackMessage(slackMessage, "C0B0LPU0SCS");

      posthog.capture('quiz_modal_completed');

      setStep(6);
    } catch (error) {
      console.error("Quiz submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const slideClass = direction === "forward" ? "animate-slide-in-right" : "animate-slide-in-left";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-5xl h-[90vh] sm:h-[620px] p-0 gap-0 overflow-hidden">
        <div className="flex flex-row h-full">
          {/* Left image — hidden on mobile and success step */}
          {step <= 5 &&
          <div className="hidden sm:block sm:w-[35%] relative flex-shrink-0 h-full">
              <img
              src={stepImages[step % stepImages.length].url}
              alt={stepImages[step % stepImages.length].name}
              className="w-full h-full object-cover transition-opacity duration-500" />
            
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5 pt-14 text-center">
                <p className="text-white font-medium text-base">{stepImages[step % stepImages.length].name}</p>
                <p className="text-white/80 text-sm flex items-center justify-center gap-1 mt-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {stepImages[step % stepImages.length].location}
                </p>
              </div>
            </div>
          }

          {/* Right content */}
          <div className={cn("flex flex-col flex-1 h-full overflow-hidden", step <= 5 ? "sm:w-[65%]" : "w-full")}>
            {/* Header - hidden on success step */}
            {step <= 5 &&
            <div className="px-8 pt-5 pb-3 space-y-3 relative shrink-0">
                <button
                onClick={handleClose}
                className="absolute top-3 right-3 p-1.5 rounded-full text-foreground hover:bg-muted transition-colors z-10"
                aria-label="Fermer">
                  <X className="h-4 w-4" />
                </button>

                {/* Stepper with badges — at the very top */}
                <div className="flex items-center gap-1.5 text-muted-foreground overflow-x-auto text-sm pr-8">
                  {STEPS.map((s, i) => {
                  const Icon = s.icon;
                  const isCompleted = i < step;
                  const isCurrent = i === step;
                  const isClickable = isCompleted && step <= 5;
                  return (
                    <React.Fragment key={s.label}>
                        {i > 0 && <span className="text-border">›</span>}
                        <span
                        className={cn("flex items-center gap-1 whitespace-nowrap transition-colors text-xs",
                        isCurrent ? "text-cta-green font-semibold" :
                        isCompleted ? "text-foreground cursor-pointer hover:text-cta-green" : ""
                        )}
                        onClick={isClickable ? () => handleStepClick(i) : undefined}
                        role={isClickable ? "button" : undefined}>
                        
                          {isCompleted ?
                        <CheckCircle className="h-3 w-3 text-cta-green" /> :

                        <Icon className="h-3 w-3" />
                        }
                          <span className="hidden sm:inline">{s.label}</span>
                        </span>
                      </React.Fragment>);

                })}
                </div>

                {/* Compatibility counter — right below stepper */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                    className="h-full bg-cta-green rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${displayedPercent}%` }} />
                  </div>
                  <span className="font-semibold text-cta-green whitespace-nowrap text-sm">
                    {displayedPercent}% match
                  </span>
                </div>

                {/* Step title */}
                <DialogTitle className="text-base font-semibold text-foreground">
                  {step === 1 ? `Quand souhaitez-vous organiser votre ${eventType === "autre" || !eventType ? "événement" : formatEventTypeLabel(eventType).toLowerCase()} ?` : STEP_TITLES[step]}
                </DialogTitle>

                {/* Step label moved to navigation bar */}
              </div>
            }

            {/* Content */}
            <div className="px-8 pb-6 h-full min-h-0 flex flex-col overflow-y-auto">

              {/* Step 0: Event type */}
              {step === 0 &&
              <div key={`step0-${animKey}`} className={cn("flex-1 flex flex-col", slideClass)}>
                  <div className="grid grid-cols-2 gap-3 auto-rows-fr">
                    {EVENT_TYPES.map((t, idx) => (
                      <button key={t.value} type="button"
                      onClick={() => {setEventType(t.value);triggerBounce(`evt-${t.value}`);}}
                      style={{ animationDelay: `${idx * 50}ms` }}
                      className={cn(
                        "flex items-center justify-center rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                        eventType === t.value ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                        bouncedItem === `evt-${t.value}` && "animate-bounce-select"
                      )}>
                          {t.label}
                        </button>
                    ))}
                    <button type="button"
                      onClick={() => {setEventType("autre");triggerBounce("evt-autre");}}
                      style={{ animationDelay: `${EVENT_TYPES.length * 50}ms` }}
                      className={cn(
                        "flex items-center justify-center rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                        eventType === "autre" ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                        bouncedItem === "evt-autre" && "animate-bounce-select"
                      )}>
                        Autre
                      </button>
                  </div>
                </div>
              }

              {/* Step 1: Dates */}
              {step === 1 &&
              <div key={`step1-${animKey}`} className={cn("space-y-4 flex-1", slideClass)}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !dateRange?.from && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ?
                      dateRange.to ?
                      `${format(dateRange.from, "d MMM yyyy", { locale: fr })} – ${format(dateRange.to, "d MMM yyyy", { locale: fr })}` :
                      format(dateRange.from, "d MMM yyyy", { locale: fr }) :
                      "Sélectionner une date ou période"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="range" selected={dateRange} onSelect={setDateRange}
                    numberOfMonths={1} className="p-3 pointer-events-auto" />
                    </PopoverContent>
                  </Popover>
                  <div>
                    <p className="text-xs text-foreground mb-2">Ou sélectionnez un mois :</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 auto-rows-fr">
                      {MONTHS.map((m, idx) =>
                    <button key={m} type="button" onClick={() => {toggleMonth(m);triggerBounce(`month-${m}`);}}
                    style={{ animationDelay: `${idx * 40}ms` }}
                    className={cn(
                      "flex items-center justify-center rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                      selectedMonth === m ? "border-cta-green bg-cta-green/10 text-foreground" : "border-border text-foreground hover:border-muted-foreground/30",
                      bouncedItem === `month-${m}` && "animate-bounce-select"
                    )}>
                          {m}
                        </button>
                    )}
                    </div>
                  </div>
                </div>
              }

              {/* Step 2: Categories */}
              {step === 2 &&
              <div key={`step2-${animKey}`} className={cn("space-y-3 flex-1", slideClass)}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 auto-rows-fr">
                    {CATEGORIES.map((c, idx) =>
                  <button key={c.label} type="button" onClick={() => toggleCategory(c.label)}
                  style={{ animationDelay: `${idx * 50}ms` }}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                    categories.includes(c.label) ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                    categories.length >= 4 && !categories.includes(c.label) && "opacity-40 cursor-not-allowed",
                    bouncedItem === `cat-${c.label}` && "animate-bounce-select"
                  )}>
                        <span className="text-base">{c.emoji}</span>
                        {c.label}
                      </button>
                  )}
                  </div>
                </div>
              }

              {/* Step 3: Participants */}
              {step === 3 &&
              <div key={`step3-${animKey}`} className={cn("flex-1 flex flex-col", slideClass)}>
                  <div className="grid grid-cols-2 gap-3 auto-rows-fr">
                    {PARTICIPANT_RANGES.map((r, idx) =>
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => {setParticipants(r.value);setCustomParticipants("");triggerBounce(`part-${r.value}`);}}
                    style={{ animationDelay: `${idx * 50}ms` }}
                    className={cn(
                      "flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                      participants === r.value ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                      bouncedItem === `part-${r.value}` && "animate-bounce-select"
                    )}>
                        <span>{r.icon}</span>
                        <span>{r.label}</span>
                      </button>
                  )}
                    {/* Autre button / inline input */}
                    <button
                      type="button"
                      style={{ animationDelay: `${PARTICIPANT_RANGES.length * 50}ms` }}
                      onClick={() => {setParticipants("custom");triggerBounce("part-custom");}}
                      className={cn(
                        "flex items-center justify-center rounded-lg border px-3 py-3 text-sm transition-colors animate-stagger-in",
                        participants === "custom" ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                        bouncedItem === "part-custom" && "animate-bounce-select"
                      )}>
                      {participants === "custom" ? (
                        <Input
                          type="number"
                          placeholder="Nombre exact"
                          min={1}
                          max={10000}
                          autoFocus
                          value={customParticipants}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {setCustomParticipants(e.target.value);}}
                          className="border-0 bg-transparent text-center text-sm p-0 h-auto focus-visible:ring-0 placeholder:text-muted-foreground" />
                      ) : "Autre"}
                    </button>
                  </div>
                </div>
              }

              {/* Step 4: Budget — Standing with stars */}
              {step === 4 &&
              <div key={`step4-${animKey}`} className={cn("flex-1 flex flex-col", slideClass)}>
                  <div className="grid grid-cols-2 gap-2 auto-rows-fr">
                    {BUDGETS.map((b, idx) => {
                      const participantCount = customParticipants ? parseInt(customParticipants) : (participants && participants !== "custom" ? (() => { const parts = participants.split("-"); return Math.round((parseInt(parts[0]) + parseInt(parts[1])) / 2); })() : 50);
                      const perPersMin = Math.round(b.min / Math.max(participantCount, 1));
                      const perPersMax = Math.round(b.max / Math.max(participantCount, 1));
                      return (
                  <button key={b.value} type="button"
                  onClick={() => {setBudget(b.value);setCustomBudget("");triggerBounce(`budget-${b.value}`);}}
                  style={{ animationDelay: `${idx * 50}ms` }}
                  className={cn(
                    "flex flex-col items-center justify-center rounded-lg border px-2 py-2 text-sm transition-colors animate-stagger-in",
                    budget === b.value ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                    bouncedItem === `budget-${b.value}` && "animate-bounce-select"
                  )}>
                        <div className="flex items-center gap-0.5 mb-0.5">
                          {Array.from({ length: b.stars }).map((_, i) =>
                      <Star key={i} className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                      )}
                        </div>
                        <span className="text-sm font-semibold">{b.amount}</span>
                        <span className="text-[11px] text-muted-foreground">{b.label}</span>
                        
                      </button>
                      );
                    })}

                    {/* Budget custom — même taille que les autres boutons */}
                    <button type="button"
                      onClick={() => {setBudget("custom");triggerBounce("budget-custom");}}
                      style={{ animationDelay: `${BUDGETS.length * 50}ms` }}
                      className={cn(
                        "flex flex-col items-center justify-center rounded-lg border px-2 py-2 text-sm transition-colors animate-stagger-in",
                        budget === "custom" ? "border-cta-green bg-cta-green/10 text-foreground font-medium" : "border-border text-foreground hover:border-muted-foreground/30",
                        bouncedItem === "budget-custom" && "animate-bounce-select"
                      )}>
                      {budget === "custom" ? (
                        <div className="relative w-full">
                          <Euro className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                          <Input
                            type="text"
                            placeholder="Ex : 25 000"
                            autoFocus
                            value={customBudget}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { setCustomBudget(e.target.value); }}
                            className="border-0 bg-transparent text-center text-sm p-0 pl-6 h-auto focus-visible:ring-0 placeholder:text-muted-foreground"
                          />
                        </div>
                      ) : (
                        <>
                          <Euro className="h-4 w-4 text-muted-foreground mb-0.5" />
                          <span className="text-sm font-semibold">Budget exact</span>
                          <span className="text-[11px] text-muted-foreground">Montant libre</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Récapitulatif animé */}
                  {(budget && budget !== "") && (
                    <div className="mt-2 rounded-lg border border-cta-green/30 bg-cta-green/5 px-3 py-2 text-sm text-foreground animate-in fade-in-0 duration-300">
                      <span className="font-medium">Budget estimé : </span>
                      {budget === "custom" ? (
                        <span className="font-semibold">{customBudget || "—"}€</span>
                      ) : (
                        <span className="font-semibold">{BUDGETS.find(b => b.value === budget)?.amount}</span>
                      )}
                      <span className="text-muted-foreground"> pour {customParticipants || (participants && participants !== "custom" ? participants : "—")} participants</span>
                    </div>
                  )}
                </div>
              }

              {/* Step 5: Votre match (email) */}
              {step === 5 &&
              <div key={`step5-${animKey}`} className={cn("flex-1 flex flex-col items-center justify-center text-center space-y-6 py-6", slideClass)}>
                  <div className="h-14 w-14 rounded-full bg-cta-green/20 flex items-center justify-center">
                    <Sparkles className="h-7 w-7 text-cta-green" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-foreground">Nous avons votre match !</h3>
                    <p className="text-sm text-foreground max-w-sm mx-auto">Indiquez-nous votre adresse e-mail professionnelle pour recevoir votre sélection. Pas de spam, c'est promis !</p>
                  </div>
                  <div className="w-full max-w-sm space-y-1.5">
                    <Input type="email" placeholder="prenom.nom@entreprise.com" value={email}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEmail(val);
                    if (emailError) setEmailError("");
                    if (val && val.includes("@") && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !isProEmail(val)) {
                      setEmailError("Veuillez renseigner un email professionnel");
                    }
                  }} />
                    {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                  </div>
                  <Button className="bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl px-8 py-3 h-auto text-base"
                disabled={!email || isSubmitting}
                onClick={handleSubmitQuiz}>
                    {isSubmitting ? "Envoi en cours..." : "Recevoir ma sélection"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1 text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> Retour
                  </Button>
                </div>
              }

              {/* Step 6: Confirmation + Confetti */}
              {step === 6 &&
              <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 py-8 animate-stagger-in">
                  <div className="h-14 w-14 rounded-full bg-cta-green/20 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-cta-green" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">Merci, c'est bien noté !</h3>
                  <p className="text-sm text-foreground max-w-sm">
                    Vous recevrez prochainement un e-mail avec votre sélection personnalisée. Gardez un œil sur votre boîte de réception !
                  </p>
                  <Button className="bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl"
                onClick={handleClose}>
                    Fermer
                  </Button>
                </div>
              }

              {/* Navigation */}
              {step < 5 &&
              <div className="flex items-center justify-between pt-6 mt-auto">
                  {step > 0 ?
                <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1 text-foreground">
                      <ArrowLeft className="h-4 w-4" /> Retour
                    </Button> :
                <span />}
                  <p className="text-xs text-muted-foreground text-center flex-1">{STEP_LABELS[step]}</p>
                  <Button size="sm" onClick={handleNext} disabled={!canProceed()}
                className="gap-1 bg-cta-green hover:bg-cta-green/90 text-cta-green-foreground rounded-xl">
                    Suivant <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              }
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

};

export default QuizModal;